import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Intercom, {
  boot as bootIntercom,
  shutdown as shutdownIntercom,
} from '@intercom/messenger-js-sdk';
import { useAuth } from '@/lib/auth';
import { authApi } from '@/api/client';
import { readConsent, subscribeConsent, type CookieConsent } from '@/lib/cookieConsent';

const INTERCOM_APP_ID = 'mp20p7va';

/**
 * LES SURFACES OÙ LE MESSAGER A LE DROIT D'EXISTER : l'agent, et rien d'autre.
 *
 * Le support client s'adresse aux opérateurs qui travaillent dans le produit,
 * pas aux visiteurs de la home, des pages produit, des pages légales ni des
 * écrans d'authentification — d'où une LISTE BLANCHE plutôt qu'une liste noire :
 * une route publique ajoutée demain n'hérite de rien, alors qu'une liste de
 * pages à exclure aurait laissé passer chaque nouveauté en silence.
 *
 * Ce sont les enfants d'`OsShell` (voir `App.tsx`) : le poste de travail et les
 * panneaux qui partagent son rail. `/dashboard/*` (la facturation héritée) n'y
 * est PAS — « uniquement sur l'agent ». Une ligne à ajouter ici si l'arbitrage
 * change.
 */
export const MESSENGER_ROUTES = ['/agent', '/spaces', '/artefacts', '/chats'] as const;

/** `/agent` et `/agent/scheduled/42` oui ; `/agents` (la redirection) non. */
export function isMessengerRoute(pathname: string): boolean {
  return MESSENGER_ROUTES.some((base) => pathname === base || pathname.startsWith(`${base}/`));
}

/**
 * Démarre le messager, qu'il ait déjà vécu dans cette session ou non.
 *
 * L'initialiseur du SDK (`Intercom(...)`) injecte le script la première fois,
 * puis se contente d'un `update` — et `update` NE RELANCE PAS un messager
 * arrêté. Or on l'arrête maintenant à chaque sortie de l'agent : sans ce
 * détour par `boot`, le lanceur disparaîtrait au premier aller-retour vers la
 * home et ne reviendrait plus jusqu'au rechargement de la page.
 */
function startMessenger(settings: Parameters<typeof bootIntercom>[0]) {
  if (typeof window !== 'undefined' && window.Intercom) {
    bootIntercom(settings);
    return;
  }
  Intercom(settings);
}

/** Éteindre ce qui n'a jamais démarré ferait juste crier le SDK dans la console. */
function stopMessenger() {
  if (typeof window !== 'undefined' && window.Intercom) shutdownIntercom();
}

/**
 * Initialises the Intercom Messenger — **dans l'agent, et une fois que
 * l'utilisateur a dit oui**.
 *
 * Le widget prouve l'identité de l'utilisateur avec un jeton signé par
 * store-back, si bien que les conversations sont rattachées à son profil dans
 * le tableau de bord Intercom et que personne ne peut se faire passer pour lui.
 *
 * LA PORTÉE. Ce provider démarrait le messager sur TOUTES les routes, visiteur
 * anonyme compris : le lanceur s'affichait donc sur la home, les pages produit
 * et les pages légales. Il est désormais réservé aux surfaces de l'agent
 * (`MESSENGER_ROUTES`) et aux utilisateurs connectés. Le mode visiteur d'Intercom
 * disparaît avec ça, et c'est voulu : les seules pages qu'un visiteur anonyme
 * peut atteindre sont précisément celles dont on retire le messager.
 *
 * Le provider reste monté au-dessus de toutes les routes, car il doit aussi
 * ÉTEINDRE : c'est en quittant l'agent qu'un widget déjà démarré doit partir.
 *
 * LE CONSENTEMENT. Intercom pose ses propres cookies tiers ; pendant ce temps
 * `/privacy` promettait en trois langues que « les cookies non essentiels ne
 * sont déposés qu'avec votre consentement ». Le bandeau
 * (`components/layout/CookieBanner.tsx`) donne ce consentement, et ce composant
 * l'attend.
 *
 * Il ÉCOUTE le consentement plutôt que de le lire une fois : le visiteur qui
 * accepte depuis le bandeau doit voir le messager apparaître sans recharger, et
 * celui qui retire son accord doit le voir disparaître — d'où le `shutdown()`,
 * qui est la seule façon propre de reprendre ce qu'on a démarré (il ferme le
 * widget ET purge la session côté client).
 *
 * L'IDENTITÉ SIGNÉE, ET POURQUOI ELLE A REMPLACÉ LES CHAMPS EN CLAIR. Ce
 * composant envoyait `user_id`, `name` et `email` directement. Intercom croit le
 * client sur parole : n'importe qui pouvait ouvrir la console et booter le
 * messager avec l'identifiant d'un autre client pour lire ses conversations
 * support. Le jeton vient maintenant de `GET /auth/intercom-token`, signé avec
 * un secret que le navigateur ne voit jamais. Quand le jeton est là, il part
 * SEUL : tout attribut envoyé à côté resterait modifiable depuis la console, et
 * Intercom les signale précisément comme « attributs non protégés ».
 *
 * LA REPLI SUR L'ANCIEN COMPORTEMENT EST VOULUE. Si l'endpoint n'existe pas
 * encore (Amplify livre le front à chaque push, le back attend les E2E) ou
 * répond `token: null` (secret non configuré), on boote avec l'identité en
 * clair — c'est-à-dire exactement ce que faisait la version précédente. Booter
 * en anonyme à la place ferait perdre son historique de conversation à un
 * utilisateur connecté, ce qui est visible et pire. Cette tolérance cesse
 * d'elle-même le jour où l'application de la sécurité est activée côté
 * Intercom : le messager rejette alors les boots non signés.
 *
 * Place this component inside <AuthProvider> so it can read auth state, and
 * inside the router so it can read the current route.
 */
export function IntercomProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());

  // Un BOOLÉEN, pas le chemin, dans les dépendances de l'effet plus bas : aller
  // de /agent à /spaces ne doit ni rallumer le messager ni redemander un jeton,
  // sans quoi chaque navigation dans le produit repartirait de zéro sous les
  // yeux de quelqu'un en pleine conversation.
  const onMessengerRoute = isMessengerRoute(pathname);

  useEffect(() => subscribeConsent(setConsent), []);

  useEffect(() => {
    if (isLoading) return;

    // Hors de l'agent, sans consentement, ou sans utilisateur connecté : on ne
    // démarre rien, et on éteint ce qui aurait été démarré plus tôt — c'est ce
    // second point qui fait disparaître le lanceur quand on quitte l'agent.
    if (!onMessengerRoute || !consent?.support || !user) {
      stopMessenger();
      return;
    }

    // `cancelled` garde contre l'aller-retour réseau qui revient APRÈS une
    // déconnexion, un retrait de consentement ou une sortie de l'agent : sans
    // lui, une réponse en vol rallumerait le messager qu'on vient d'éteindre.
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const bootUnsigned = () => {
      startMessenger({
        app_id: INTERCOM_APP_ID,
        user_id: user.id,
        name: user.name,
        email: user.email,
        ...(user.createdAt
          ? { created_at: Math.floor(new Date(user.createdAt).getTime() / 1000) }
          : {}),
      });
    };

    const bootSigned = async () => {
      try {
        const { data } = await authApi.intercomToken();
        if (cancelled) return;

        if (!data?.token) {
          bootUnsigned();
          return;
        }

        startMessenger({ app_id: INTERCOM_APP_ID, intercom_user_jwt: data.token });

        // Le jeton expire (une heure côté serveur) alors qu'un onglet de support
        // reste ouvert bien plus longtemps. On le renouvelle à 80 % de sa vie :
        // sans ça la session du messager tombe en pleine conversation, et le
        // visiteur n'a aucun moyen de comprendre pourquoi.
        const lifetime = Math.max(60, Math.floor((data.expiresIn || 3600) * 0.8));
        refreshTimer = setTimeout(() => void bootSigned(), lifetime * 1000);
      } catch {
        // Endpoint absent, hors ligne, jeton d'accès périmé : voir « LE REPLI »
        // ci-dessus. Silencieux à dessein — un widget de support qui hurle dans
        // la console n'aide personne.
        if (!cancelled) bootUnsigned();
      }
    };

    void bootSigned();

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [user, isLoading, consent, onMessengerRoute]);

  return <>{children}</>;
}
